import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../../../utils/csrf';
import { FormData } from '../../models/ModelInvestments';

export const useInvestmentsCrypto = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showCustomTicker, setShowCustomTicker] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    assetType: 'digital-coin',
    operationType: '',
    currency: '',
    operationDate: new Date().toISOString().split('T')[0],
    ticker: '',
    volume: '',
    price: '',
  });

  const totalSteps = 6;

  const handleSelectOption = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'ticker' && value === 'other') {
      setShowCustomTicker(true);
    } else if (field === 'ticker') {
      setShowCustomTicker(false);
    } else if (field === 'confirm') {
      if (value === 'no') {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'ticker' && value && showCustomTicker) {
      setCurrentStep(5);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const requiredFields: (keyof FormData)[] = [
      'assetType',
      'operationType',
      'currency',
      'operationDate',
      'ticker',
      'volume',
      'price'
    ];

    const emptyFields = requiredFields.filter(field => !formData[field]);
    if (emptyFields.length > 0) {
      console.error('Missing required fields:', emptyFields);
      return;
    }

    try {
      let csrfToken;
      try {
        csrfToken = await getCsrfToken();
      } catch (error) {
        console.error('Error getting CSRF token:', error);
        // Try to get it from cookies directly as fallback
        csrfToken = document.cookie.split('; ')
          .find(row => row.startsWith('csrftoken='))
          ?.split('=')[1];
        
        if (!csrfToken) {
          throw new Error('Could not get CSRF token');
        }
      }

      const response = await fetch('/api/investments/import/crypto/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({
          ...formData,
          volume: parseFloat(formData.volume),
          price: parseFloat(formData.price),
          operationType: formData.operationType.toLowerCase(),
        }),
      });

      let data;
      const textResponse = await response.text();
      try {
        data = textResponse ? JSON.parse(textResponse) : {};
      } catch (e) {
        console.error('Failed to parse response:', textResponse);
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit form');
      }

      if (data.success) {
        navigate('/investments');
      } else {
        throw new Error(data.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  };

  const navigateToStep = (step: number) => {
    if (step > currentStep) {
      let canProceed = true;
      
      switch (currentStep) {
        case 1:
          canProceed = !!formData.operationType;
          break;
        case 2:
          canProceed = !!formData.currency;
          break;
        case 3:
          canProceed = !!formData.operationDate;
          break;
        case 4:
          canProceed = !!formData.ticker;
          break;
        case 5:
          canProceed = !!formData.volume && !!formData.price;
          break;
      }

      if (!canProceed) {
        console.warn('Please fill in all required fields before proceeding');
        return;
      }
    }
    
    setCurrentStep(step);
  };

  return {
    currentStep,
    formData,
    showCustomTicker,
    totalSteps,
    handleInputChange,
    handleSelectOption,
    navigateToStep,
    handleSubmit,
  };
};
